import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import config from './../config/config';
import { cacheUtility } from './cacheUtils';
import { useNavigate } from 'react-router-dom';
import Auth from './auth';
import endpoints from './../config/endpoints';
import Cookies from 'js-cookie';
import { encodeWithKeyAndShift, decryptData, decodeWithKeyAndShift } from './base64Utils';
import { errorLogger, infoLogger } from './applicationLogUtils';
import { toast } from 'react-toastify';
import { performRefreshWithRetry } from './refreshTokenService';
import { clearServiceRuleData } from '@store/actions/serviceRuleMasterActions';
import { store } from '@store/index';
import { getIsRefreshing, setIsRefreshing, enqueueRequest, processQueue } from './tokenRefreshState';
import proactiveTokenRefresh from './proactiveTokenRefresh';
import { handleApiError } from '../auth/handleApiError';
import { showAuthServiceDownModal, showLoginRequiredModal, showModalforMultiDeviceRestriction } from '../auth/authErrorPresenter';
import { parseApiError } from '../auth/errorContract';
/** Browser-safe encoded string generator for temporary tracing */

const AUTH_ENDPOINTS = [endpoints.userLogin, endpoints.beneficiaryLogin, endpoints.userLogout, endpoints.beneficiaryLogout, endpoints.refreshTokken, endpoints.beneficiaryRefreshToken];
const PUBLIC_ENDPOINTS = ['/beneficiary-self-registration/consent/', endpoints.beneficiaryAbhaSelfRegistrationVerifyOtp, endpoints.beneficiaryAbhaSelfRegistrationSendOtp, endpoints.beneficiaryAbhaExists, ];

function generateEncodedString(last4Phone: string, opts: { isAndroidApp?: boolean, date?: Date } = {}) {

    const { isAndroidApp = false, date = new Date() } = opts;



    // 1) Browser/app code

    let code = '9';

    if (isAndroidApp) {

        code = '0';

    } else {

        const ua = (navigator && navigator.userAgent) ? navigator.userAgent : '';

        if (/Edg/i.test(ua)) code = '2';

        else if (/Firefox/i.test(ua)) code = '3';

        else if (/Chrome/i.test(ua)) code = '1';

    }



    // 2) Last-4 normalization

    const phoneStr = String(last4Phone).replace(/\D/g, '').slice(-4).padStart(4, '0');



    // 3) ddmmyyhhMMss (local time)

    const dd = String(date.getDate()).padStart(2, '0');

    const mm = String(date.getMonth() + 1).padStart(2, '0');

    const yy = String(date.getFullYear() % 100).padStart(2, '0');

    const hh = String(date.getHours()).padStart(2, '0');

    const MM = String(date.getMinutes()).padStart(2, '0');

    const ss = String(date.getSeconds()).padStart(2, '0');



    // 4) milliseconds

    const msc = String(date.getMilliseconds()).padStart(3, '0');



    const encoded = code + phoneStr + dd + mm + yy + hh + MM + ss + msc;

    if (encoded.length !== 20) {

        throw new Error(`Encoded string must be 20 chars, got ${encoded.length}: ${encoded}`);

    }

    return encoded;

}

const getLoggedInUser = () => {
  try {
    const encoded = localStorage.getItem('userDetails');
    if (!encoded) {
      return {};
    }
    const decoded = decodeWithKeyAndShift(encoded);
    return JSON.parse(decoded);
  } catch (error) {
    return {};
  }
};

// Extend Axios with custom methods
const extendAxios = (axiosInstance: any) => {
    axiosInstance.getSmart = (url: string, config?: AxiosRequestConfig) => {
        return axiosInstance.get(url, config);
    };

    axiosInstance.postSmart = (url: string, data?: any, config?: AxiosRequestConfig) => {
       
        return axiosInstance.post(url, data, config);
    };

    axiosInstance.putSmart = (url: string, data?: any, config?: AxiosRequestConfig) => {
       
        return axiosInstance.put(url, data, config);
    };

    axiosInstance.deleteSmart = (url: string, config?: AxiosRequestConfig) => {
       
        return axiosInstance.delete(url, config);
    };
};

const getWithCache = async (url: string, axiosInst:any, config: AxiosRequestConfig) => {
    const cacheKey = `${url}`;
    // ${JSON.stringify(config)}
    const cachedData = cacheUtility.get<any>(cacheKey);

    if (cachedData) {
        return Promise.resolve(cachedData);
    } else {

            const response = await axiosInst.get(url, config);
            cacheUtility.set(cacheKey, response.data);
            return Promise.resolve(response);

    }

};

const postWithCache = async (url: string, axiosInst: any, data?: any, config?: AxiosRequestConfig) => {
    const cacheKey = `${url}_${JSON.stringify(data || {})}`;
    const cachedData = cacheUtility.get<any>(cacheKey);

    if (cachedData) {
        return Promise.resolve(cachedData);
    } else {
        const response = await axiosInst.post(url, data, config);
        cacheUtility.set(cacheKey, response.data);
        return Promise.resolve(response);
    }
};

const axiosProxy = (axiosInstance: any) => {
    const proxy:any={};
    proxy.get = (url: string, config?: AxiosRequestConfig) => {
        return axiosInstance.get(url, config);
    };
    proxy.getWithCache = (url: string, config?: AxiosRequestConfig) => {
        return getWithCache(url, axiosInstance,config? config : {} );
    };
    
    proxy.post = (url: string, data?: any, config?: AxiosRequestConfig) => {
       
        return axiosInstance.post(url, data, config);
    };

    proxy.postWithCache = (url: string, data?: any, config?: AxiosRequestConfig) => {
        return postWithCache(url, axiosInstance, data, config);
    };

    proxy.put = (url: string, data?: any, config?: AxiosRequestConfig) => {
       
        return axiosInstance.put(url, data, config);
    };

    proxy.patch = (url: string, data?: any, config?: AxiosRequestConfig) => {
       
        return axiosInstance.patch(url, data, config);
    };

    proxy.delete = (url: string, config?: AxiosRequestConfig) => {
       
        return axiosInstance.delete(url, config);
    };
    proxy.setHeaders = (headers: any) => {
        axiosInstance.defaults.headers.common = { ...axiosInstance.defaults.headers.common, ...headers };
    };
    // Expose axios static helpers on the proxy so consumers importing this
    // module (instead of the raw axios lib) can still use them, e.g.
    // `axios.isAxiosError(error)`.
    proxy.isAxiosError = axios.isAxiosError;
    return proxy;
};

const BASE_URL = `${config.baseURL}`;
const axiosInstance = axios.create({
    baseURL: BASE_URL,
});

axiosInstance.interceptors.request.use(async (value) => {
    const requestUrl = value?.url || '';
    // Do not block authentication-related endpoints.
    const isAuthEndpoint = AUTH_ENDPOINTS.some((endpoint) =>
        requestUrl.includes(endpoint)
    );
    // Pause newly created requests while token refresh is running.
    if (getIsRefreshing() && !isAuthEndpoint) {
        const token = await new Promise<string>((resolve, reject) => {
            enqueueRequest({ resolve, reject });
        });
        value.headers = value.headers || {};
        // Use the refreshed user token.
        (value.headers as any)['Authorization'] = `Bearer ${token}`;
        // Use the refreshed WSO2/client token as well.
    }

    const userDetails: any = getLoggedInUser();

    // Attach userId (if available in localStorage) to every outgoing request.
    const userId = localStorage.getItem('userId');
    const benefId = localStorage.getItem('beneficiaryId');
    const isBeneficiaryLogged = localStorage.getItem('isBeneficiaryLogged') === 'true';
    const isBeneficiaryLoggedRoute = requestUrl.includes('/benef');
    value.headers = value.headers ?? {};

    //if route is beneficiary login then attach benefId else attach userId
    if (benefId && (isBeneficiaryLoggedRoute || isBeneficiaryLogged)) {
        value.headers['id'] = benefId;
        value.headers['idtype'] = 'b'
    } else if (userId) {
        value.headers['id'] = userId;
        value.headers['idtype'] = 'u'
    }
    // Check if temporary tracing is enabled

    if (config?.tempTracing === "ON") {

        // Generate once per request

        const last4Phone = userDetails?.mob ? String(userDetails.mob).slice(-4) : '0000';
        const tt = generateEncodedString(last4Phone);



        // Ensure params object exists

        const params = value.params ? { ...value.params } : {};

        // If caller already set _tt, we overwrite (or switch to set-if-absent)

        params._tt = tt;

        value.params = params;

    }

    return value;
}, (error) => {

    return Promise.reject(error);

});
//const navigate = useNavigate();

/**
 * Capped exponential backoff retry for 503 AUTH_SERVICE_UNAVAILABLE responses.
 * Keeps the session intact; retries the ORIGINAL request with delays
 * 1s, 2s, 4s, 8s, capped at 15s, up to 4 attempts per URL, then gives up.
 */
const backoffCounts = new Map<string, number>();
async function retryWithBackoff(error: any): Promise<any> {
    const url = error.config?.url || '';
    const n = backoffCounts.get(url) || 0;
    if (n >= 4) {
        backoffCounts.set(url, 0);
        return Promise.reject(error);
    }
    const delay = Math.min(1000 * 2 ** n, 15000);
    backoffCounts.set(url, n + 1);
    await new Promise((r) => setTimeout(r, delay));
    try {
        const res = await axiosInstance(error.config);
        backoffCounts.set(url, 0);
        return res;
    } catch (retryError) {
        return retryWithBackoff(retryError);
    }
}

axiosInstance.interceptors.response.use(
    async (response) => {
        if (AUTH_ENDPOINTS.some(ep => response?.config?.url?.includes(ep))) return response;
        const isBeneficiaryLogged = localStorage.getItem('isBeneficiaryLogged') || 'false';
        const currentDate  = Number(new Date());
        //currentDate = new Date(currentDate.getTime() + 0);
        const currentTime  = new Date().getTime();
        const sessionExpiryTime = Cookies.get('expiry');             
        if(sessionExpiryTime){
            if(currentDate > Number(sessionExpiryTime)){
                if (getIsRefreshing()) return response;
                setIsRefreshing(true);
                try {
                    const result = await performRefreshWithRetry();
                    if (result.success) {
                        const data = result.data;
                        const permissions: any = encodeWithKeyAndShift(JSON.stringify(data?.access_list ?? ''));
                        const timeObject = new Date(new Date().getTime() + data.auth_iam_status.expires_in * 1000);
                        Auth.refreshToken(Number(timeObject), data.auth_iam_status.access_token, data.auth_iam_status.refresh_token, permissions, data.user_id, data.usertype_id);
                        store.dispatch(clearServiceRuleData('hrp-rules'));
                        processQueue(null, data.auth_iam_status.access_token);
                    } else {
                        processQueue(result.error, null);
                        proactiveTokenRefresh.stopProactiveRefresh();
                        toast.error('Session renewal failed. Logging you out...', { position: 'top-center' });
                        setTimeout(() => { Auth.refreshFails(); }, 500);
                    }
                } finally {
                    setIsRefreshing(false);
                }

            }
        }

       // }
       // sessionExpiryTime?((currentDate< sessionExpiryTime)?Auth.logIn():null):null;
         return response
    }, // Pass through successful responses
    async (error: any) => {
        const originalRequest = error.config;
        if (AUTH_ENDPOINTS.some(ep => originalRequest?.url?.includes(ep)) || PUBLIC_ENDPOINTS.some(ep => originalRequest?.url?.includes(ep))) {
            return Promise.reject(error);
        }


      // --- Wrapped auth-contract handling (isolated, non-breaking) ---
      // Returns 'PASS' for any non-wrapped error, so existing behavior below runs unchanged.
      const decision = handleApiError(error);
      if (decision === 'HANDLED') {
        // Toast / 400 already surfaced by the orchestrator.
        return Promise.reject(error);
      }
      if (decision === 'RETRY_LATER') {
        // 503 auth-service unavailable: capped backoff, session kept intact.
        return retryWithBackoff(error);
      }
      if (decision === 'LOGIN_REQUIRED') {
        // Non-refreshable / corrupted credentials (401 INVALID_TOKEN, SESSION_*,
        // or 400 AUTH_TOKEN_MALFORMED): show a blocking popup FIRST; only on OK
        // do we clear the local token and redirect to login. Never redirect first.
        proactiveTokenRefresh.stopProactiveRefresh();
        const parsed = parseApiError(error);
        showLoginRequiredModal(parsed?.messageDescription, parsed?.correlationId);
        return Promise.reject(error);
      }
      // decision === 'REFRESH' (401 TOKEN_EXPIRED) or 'PASS' -> fall through to the
      // existing refresh/retry/queue logic below (unchanged).

      if (error.response?.status === 401 && !originalRequest?._retry) {
        if (getIsRefreshing()) {
            // queue this request; it will be replayed once refresh completes
            originalRequest._retry = true;
            return new Promise((resolve, reject) => {
                enqueueRequest({ resolve, reject });
            }).then((token) => {
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                return axiosInstance(originalRequest);

            }).catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        setIsRefreshing(true);

        try {
            const result = await performRefreshWithRetry();
            if (result.success) {
                const data = result.data;
                const permissions: any = encodeWithKeyAndShift(JSON.stringify(data?.access_list ?? ''));
                const timeObject = new Date(new Date().getTime() + data.auth_iam_status.expires_in * 1000);
                const newToken = data.auth_iam_status.access_token;
                Auth.refreshToken(Number(timeObject), newToken, data.auth_iam_status.refresh_token, permissions, data.user_id, data.usertype_id);
                store.dispatch(clearServiceRuleData('hrp-rules'));
                processQueue(null, newToken);
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return axiosInstance(originalRequest);
            } else {
                processQueue(result.error, null);
                proactiveTokenRefresh.stopProactiveRefresh();
                // Refresh exhausted its retries -> blocking modal; OK logs the user out.
                showModalforMultiDeviceRestriction(parseApiError(error)?.correlationId);
            }
        } catch(err: any){
            processQueue(err, null);
            proactiveTokenRefresh.stopProactiveRefresh();
            errorLogger(err?.response?.data || err?.response || err);
            // Refresh exhausted its retries -> blocking modal; OK logs the user out.
            showAuthServiceDownModal(parseApiError(error)?.correlationId);
        } finally {
            setIsRefreshing(false);
        }
      }
      return Promise.reject(error);
});

export default axiosProxy(axiosInstance as any); 
